import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import CampaignCard from '../components/campaign/CampaignCard';
import HomeHero from '../components/home/HomeHero';
import { supabase } from '../services/supabase';
import FilterBar from '../components/common/FilterBar';
import { differenceInHours, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import { ANALYTICS_EVENTS, trackEvent, trackPageView } from '../utils/analytics';

interface Campaign {
  id: string;
  image: string;
  title: string;
  author: string;
  source?: string;
  source_url?: string;
  date: Date;
  description: string;
  upvotes: number;
  downvotes: number;
  vote_score: number;
  trending_score?: number | null;
  category?: string;
  is_sponsored: boolean;
  sponsor_name?: string | null;
  sponsor_url?: string | null;
}

type SortType = 'Recent' | 'Trending';

const PAGE_SIZE = 30;
const OBSERVER_ROOT_MARGIN = '800px 0px';
const SITE_URL = 'https://ballotr.com';
const PAGE_TITLE = 'Ballotr | Engage. Decide. Act.';
const PAGE_DESCRIPTION =
  'Ballotr turns political news into action. Read the issue, take a side, and connect your stance to organizations tied to the outcome.';
const PAGE_IMAGE = `${SITE_URL}/logo192.png`;

const Home: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [sortBy, setSortBy] = useState<SortType>('Recent');
  const [selectedSources, setSelectedSources] = useState<string[]>([]);

  const [cursorCreatedAt, setCursorCreatedAt] = useState<string | null>(null);
  const [cursorId, setCursorId] = useState<string | null>(null);
  const [cursorTrendingScore, setCursorTrendingScore] = useState<number | null>(null);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const zeroResultTrackedKeyRef = useRef<string | null>(null);

  const maybeShowPendingToast = useCallback(() => {
    try {
      if (document.visibilityState !== 'visible') return;

      const msg = sessionStorage.getItem('ballotr_next_toast');
      if (msg) {
        sessionStorage.removeItem('ballotr_next_toast');
        toast.success(msg);
      }
    } catch {
      // Ignore storage errors.
    }
  }, []);

  useEffect(() => {
    maybeShowPendingToast();
    window.addEventListener('focus', maybeShowPendingToast);
    document.addEventListener('visibilitychange', maybeShowPendingToast);

    return () => {
      window.removeEventListener('focus', maybeShowPendingToast);
      document.removeEventListener('visibilitychange', maybeShowPendingToast);
    };
  }, [maybeShowPendingToast]);

  useEffect(() => {
    trackPageView('home');
  }, []);

  const handleSortChange = (val: string) => {
    if (val === 'Recent' || val === 'Trending') {
      trackEvent(ANALYTICS_EVENTS.SORT_CHANGED, {
        previous_sort: sortBy,
        next_sort: val,
      });

      setSortBy(val);
    }
  };

  const handleSourceChange = (sources: string[]) => {
    trackEvent(ANALYTICS_EVENTS.SOURCE_FILTER_CHANGED, {
      selected_source_count: sources.length,
      selected_sources: sources,
    });

    setSelectedSources(sources);
  };

  const handleClearFilters = () => {
    trackEvent(ANALYTICS_EVENTS.FILTERS_CLEARED, {
      previous_sort: sortBy,
      previous_selected_source_count: selectedSources.length,
      previous_selected_sources: selectedSources,
    });

    setSortBy('Recent');
    setSelectedSources([]);
  };

  const getAgeLabel = (date: Date) => {
    const hours = differenceInHours(new Date(), date);
    if (hours < 24) return `${hours}h ago`;

    const days = differenceInDays(new Date(), date);
    return `${days}d ago`;
  };

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      if (
        selectedSources.length > 0 &&
        (!campaign.source || !selectedSources.includes(campaign.source))
      ) {
        return false;
      }

      return true;
    });
  }, [campaigns, selectedSources]);

  const deriveNextCursorFromPage = useCallback(
    (page: Campaign[]) => {
      for (let i = page.length - 1; i >= 0; i--) {
        if (!page[i].is_sponsored) {
          return {
            createdAt: page[i].date.toISOString(),
            id: page[i].id,
            trendingScore: sortBy === 'Trending' ? page[i].trending_score ?? 0 : null,
          };
        }
      }

      return null;
    },
    [sortBy]
  );

  const mapRowsToCampaigns = (data: any[]): Campaign[] => {
    return (data ?? []).map((row: any) => ({
      id: row.id,
      image: row.image,
      title: row.title,
      author: row.author_name,
      source: row.source ?? undefined,
      source_url: row.source_url ?? undefined,
      date: new Date(row.created_at),
      description: row.description,
      upvotes: row.upvotes ?? 0,
      downvotes: row.downvotes ?? 0,
      vote_score: row.vote_score ?? 0,
      trending_score:
        row.trending_score === null || row.trending_score === undefined
          ? null
          : Number(row.trending_score),
      category: row.category ?? undefined,
      is_sponsored: !!row.is_sponsored,
      sponsor_name: row.sponsor_name ?? null,
      sponsor_url: row.sponsor_url ?? null,
    }));
  };

  const fetchFirstPage = useCallback(async () => {
    setLoading(true);
    setHasMore(true);
    setCursorCreatedAt(null);
    setCursorId(null);
    setCursorTrendingScore(null);

    try {
      const { data, error } = await supabase.rpc('rpc_get_home_feed', {
        p_limit: PAGE_SIZE,
        p_cursor_created_at: null,
        p_cursor_id: null,
        p_cursor_trending_score: null,
        p_ratio: 8,
        p_max_sponsored_pct: 20,
        p_min_source_distance: 1,
        p_seed: null,
        p_sort_mode: sortBy === 'Trending' ? 'trending' : 'recent',
      });

      if (error) throw error;

      const page = mapRowsToCampaigns(data ?? []);
      setCampaigns(page);

      trackEvent(ANALYTICS_EVENTS.FEED_LOADED, {
        sort_mode: sortBy.toLowerCase(),
        result_count: page.length,
        has_results: page.length > 0,
        selected_source_count: selectedSources.length,
        selected_sources: selectedSources,
      });

      if (page.length === 0) {
        setHasMore(false);
        return;
      }

      const next = deriveNextCursorFromPage(page);

      if (next) {
        setCursorCreatedAt(next.createdAt);
        setCursorId(next.id);
        setCursorTrendingScore(next.trendingScore);
        setHasMore(true);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching feed:', err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [deriveNextCursorFromPage, selectedSources, sortBy]);

  const fetchNextPage = useCallback(
    async (trigger: 'scroll' | 'button' = 'scroll') => {
      if (loading || loadingMore || !hasMore) return;

      if (!cursorCreatedAt || !cursorId) {
        setHasMore(false);
        return;
      }

      if (sortBy === 'Trending' && cursorTrendingScore === null) {
        setHasMore(false);
        return;
      }

      trackEvent(ANALYTICS_EVENTS.FEED_LOAD_MORE, {
        trigger,
        sort_mode: sortBy.toLowerCase(),
        current_loaded_count: campaigns.length,
        selected_source_count: selectedSources.length,
        selected_sources: selectedSources,
      });

      setLoadingMore(true);

      try {
        const { data, error } = await supabase.rpc('rpc_get_home_feed', {
          p_limit: PAGE_SIZE,
          p_cursor_created_at: cursorCreatedAt,
          p_cursor_id: cursorId,
          p_cursor_trending_score: sortBy === 'Trending' ? cursorTrendingScore : null,
          p_ratio: 8,
          p_max_sponsored_pct: 20,
          p_min_source_distance: 1,
          p_seed: null,
          p_sort_mode: sortBy === 'Trending' ? 'trending' : 'recent',
        });

        if (error) throw error;

        const page = mapRowsToCampaigns(data ?? []);

        if (page.length === 0) {
          setHasMore(false);
          return;
        }

        const next = deriveNextCursorFromPage(page);

        setCampaigns((prev) => {
          const seen = new Set(prev.map((campaign) => campaign.id));
          const merged = [...prev];

          for (const item of page) {
            if (!seen.has(item.id)) {
              merged.push(item);
            }
          }

          return merged;
        });

        if (!next) {
          setHasMore(false);
          return;
        }

        const cursorDidAdvance =
          next.createdAt !== cursorCreatedAt ||
          next.id !== cursorId ||
          (sortBy === 'Trending' && next.trendingScore !== cursorTrendingScore);

        if (!cursorDidAdvance) {
          setHasMore(false);
          return;
        }

        setCursorCreatedAt(next.createdAt);
        setCursorId(next.id);
        setCursorTrendingScore(next.trendingScore);
        setHasMore(true);
      } catch (err) {
        console.error('Error fetching next feed page:', err);
        setHasMore(false);
      } finally {
        setLoadingMore(false);
      }
    },
    [
      campaigns.length,
      cursorCreatedAt,
      cursorId,
      cursorTrendingScore,
      deriveNextCursorFromPage,
      hasMore,
      loading,
      loadingMore,
      selectedSources,
      sortBy,
    ]
  );

  useEffect(() => {
    fetchFirstPage();
  }, [fetchFirstPage]);

  useEffect(() => {
    if (loading) return;
    if (campaigns.length === 0) return;
    if (filteredCampaigns.length > 0) return;

    const trackingKey = JSON.stringify({
      sortBy,
      selectedSources: [...selectedSources].sort(),
      totalCampaigns: campaigns.length,
    });

    if (zeroResultTrackedKeyRef.current === trackingKey) return;

    zeroResultTrackedKeyRef.current = trackingKey;

    trackEvent(ANALYTICS_EVENTS.ZERO_RESULT_FILTERED_STATE, {
      sort_mode: sortBy.toLowerCase(),
      selected_source_count: selectedSources.length,
      selected_sources: selectedSources,
      total_campaign_count: campaigns.length,
    });
  }, [loading, campaigns.length, filteredCampaigns.length, selectedSources, sortBy]);

  useEffect(() => {
    const node = loadMoreRef.current;

    if (!node) return;
    if (loading) return;
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry.isIntersecting) return;
        if (loading || loadingMore || !hasMore) return;

        fetchNextPage('scroll');
      },
      {
        root: null,
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: 0,
      }
    );

    observer.observe(node);

    const rect = node.getBoundingClientRect();

    if (rect.top < window.innerHeight + 800 && hasMore && !loading && !loadingMore) {
      fetchNextPage('scroll');
    }

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, loading, loadingMore, hasMore]);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={`${SITE_URL}/`} />

        <meta property="og:site_name" content="Ballotr" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:image" content={PAGE_IMAGE} />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
      </Helmet>

      <main className="bg-secondary min-h-screen py-6 px-4 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-[1360px]">
          <HomeHero />

          <div id="campaign-feed" className="scroll-mt-28">
            <FilterBar
              sortBy={sortBy}
              onSortChange={handleSortChange}
              selectedSources={selectedSources}
              onSourceChange={handleSourceChange}
              onClearFilters={handleClearFilters}
            />

            <div className="grid grid-cols-1 gap-6 mt-4 md:grid-cols-2 xl:grid-cols-3">
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="bg-gray-200 h-96 rounded-lg animate-pulse" />
                ))
              ) : filteredCampaigns.length > 0 ? (
                filteredCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    id={campaign.id}
                    image={campaign.image}
                    title={campaign.title}
                    author={campaign.author}
                    source={campaign.source}
                    sourceUrl={campaign.source_url}
                    date={campaign.date}
                    description={campaign.description}
                    category={campaign.category}
                    ageLabel={getAgeLabel(campaign.date)}
                    isSponsored={campaign.is_sponsored}
                    sponsorName={campaign.sponsor_name ?? undefined}
                    sponsorUrl={campaign.sponsor_url ?? undefined}
                  />
                ))
              ) : (
                <div className="text-center col-span-full text-gray-600">
                  No campaigns found. Try adjusting your filters.
                </div>
              )}
            </div>

            {!loading && hasMore && (
              <div className="mt-8">
                <div ref={loadMoreRef} className="h-32 w-full" />

                <div className="flex justify-center">
                  <button
                    onClick={() => fetchNextPage('button')}
                    disabled={loadingMore}
                    className="px-6 py-3 rounded-lg bg-white shadow hover:shadow-md border border-gray-200 text-textPrimary disabled:opacity-60"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default Home;